import { useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowLeft, Mail } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function PasswordResetRequest() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [devLink, setDevLink] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await api.post("/auth/password-reset/request", { email });
      setSent(true);
      // Email service is not configured — surface the link directly so user can complete the reset.
      if (r.data?.dev_reset_link) setDevLink(r.data.dev_reset_link);
    } catch {
      toast.error("Could not send reset link");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8] flex flex-col">
      <header className="container-bsp h-16 flex items-center">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-[#1A1F3D] flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-[#D4A853]" />
          </div>
          <span className="font-display text-xl text-[#1A1F3D]">BrandSnapPro™</span>
        </Link>
      </header>
      <main className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md bsp-card p-8 md:p-10" data-testid="reset-request-card">
          {!sent ? (
            <>
              <span className="eyebrow">Forgot password</span>
              <h1 className="font-display text-3xl text-[#1A1F3D] mt-3">Reset your password</h1>
              <p className="text-[#52525b] mt-2">Enter the email you signed up with and we'll send you a link to set a new password.</p>
              <form onSubmit={submit} className="mt-7 space-y-4" data-testid="reset-request-form">
                <div>
                  <label className="text-sm font-medium text-[#1A1F3D]">Email</label>
                  <input type="email" required className="bsp-input mt-1" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="reset-email" />
                </div>
                <button type="submit" disabled={submitting} className="btn-primary w-full justify-center" data-testid="reset-submit">
                  {submitting ? "Sending…" : "Send reset link"}
                </button>
              </form>
            </>
          ) : (
            <div data-testid="reset-sent">
              <Mail className="w-9 h-9 text-[#D4A853]" />
              <h1 className="font-display text-2xl text-[#1A1F3D] mt-3">Check your email</h1>
              <p className="text-[#52525b] mt-2">If an account exists for {email || "that address"}, we've sent a link to reset your password. The link expires in 24 hours.</p>
              {devLink && (
                <div className="mt-5 p-4 rounded-lg bg-[#F5E6E0]/60 border border-[#D4A853]/30 text-sm">
                  <div className="font-semibold text-[#1A1F3D]">Email service isn't configured yet.</div>
                  <div className="text-[#2D2D2D] mt-1">Use this link to reset your password right now:</div>
                  <a href={devLink} className="mt-2 block break-all gold-link text-xs font-mono" data-testid="dev-reset-link">{devLink}</a>
                </div>
              )}
            </div>
          )}
          <div className="mt-6 text-sm">
            <Link to="/login" className="text-[#52525b] inline-flex items-center gap-1.5 hover:text-[#1A1F3D]">
              <ArrowLeft className="w-4 h-4" /> Back to login
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
